
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { DateRangeSelector } from "./components/DateRangeSelector";
import { ExportDialog } from "./components/ExportDialog";
import { AnalyticsSettings } from "./components/AnalyticsSettings";
import { useDateRangeFilter } from "./hooks/useDateRangeFilter";
import { useAnalyticsSettings } from "./hooks/useAnalyticsSettings";
import { toast } from "sonner";
import type { Candidate } from "@/components/candidates/types";
import type { AnalyticsConfig } from "./components/AnalyticsSettings";

interface AnalyticsHeaderProps {
  onDateRangeChange?: (range: { from: Date; to: Date }) => void;
  onRefresh?: () => void;
  onSettingsChange?: (settings: AnalyticsConfig) => void;
  isLoading?: boolean;
  candidates: Candidate[];
}

export const AnalyticsHeader = ({ 
  onDateRangeChange, 
  onRefresh,
  onSettingsChange,
  isLoading = false,
  candidates
}: AnalyticsHeaderProps) => {
  const { 
    dateRange, 
    setDateRange, 
    presetRanges, 
    setPresetRange 
  } = useDateRangeFilter();
  
  const { settings, updateSettings } = useAnalyticsSettings();

  const handleDateRangeChange = (newRange: { from: Date; to: Date }) => {
    setDateRange(newRange);
    onDateRangeChange?.(newRange);
  };

  const handlePresetSelect = (preset: string) => {
    setPresetRange(preset);
    const presetRange = presetRanges.find(p => p.value === preset)?.range;
    if (presetRange) {
      onDateRangeChange?.(presetRange);
    }
  };

  const handleRefresh = () => {
    onRefresh?.();
    toast.success("Analytics data refreshed");
  };

  const handleSettingsChange = (newSettings: AnalyticsConfig) => {
    updateSettings(newSettings);
    onSettingsChange?.(newSettings);
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Analytics Dashboard</h1>
        <p className="text-muted-foreground mt-1">Track your recruitment performance and insights</p>
      </div>
      
      <div className="flex items-center gap-3 flex-wrap">
        <DateRangeSelector
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          presetRanges={presetRanges}
          onPresetSelect={handlePresetSelect}
        />
        
        <ExportDialog candidates={candidates} dateRange={dateRange} />
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        
        <AnalyticsSettings 
          settings={settings}
          onSettingsChange={handleSettingsChange}
        />
      </div>
    </div>
  );
};
